import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, ArrowUp, ArrowDown } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { AdminTitle, Btn, Field, inputCls } from "@/pages/admin/Admin";
import { Uploader } from "@/pages/admin/Products";
import { Switch } from "@/components/ui/switch";

const Card = ({ title, children, testid }) => (
  <section className="bg-white border border-[#E6DFD5] p-6 space-y-4" data-testid={testid}><h2 className="font-serif text-2xl">{title}</h2>{children}</section>
);

function ImageField({ value, onChange, testid }) {
  return (
    <div className="flex gap-3 items-center">
      {value && <img src={fileUrl(value)} alt="" className="w-20 h-14 object-cover" />}
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" className={inputCls} data-testid={`${testid}-url`} />
      <Uploader accept="image/*" onDone={(d) => onChange(d.url)} testid={`${testid}-upload`} />
    </div>
  );
}

function ListControls({ list, i, set }) {
  const swap = (a, b) => { const n = [...list]; [n[a], n[b]] = [n[b], n[a]]; set(n); };
  return (
    <div className="flex gap-2">
      <button disabled={i === 0} onClick={() => swap(i, i - 1)} aria-label="Up" className="disabled:opacity-30"><ArrowUp size={14} /></button>
      <button disabled={i === list.length - 1} onClick={() => swap(i, i + 1)} aria-label="Down" className="disabled:opacity-30"><ArrowDown size={14} /></button>
      <button onClick={() => set(list.filter((_, j) => j !== i))} aria-label="Delete"><Trash2 size={14} /></button>
    </div>
  );
}

const FONTS = ["Libre Baskerville", "Baskervville", "Source Serif 4", "Merriweather", "Lora", "PT Serif", "Crimson Pro", "Cormorant Garamond", "Playfair Display"];

function Socials({ list, set }) {
  const upd = (i, patch) => set(list.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div className="space-y-2" data-testid="website-socials-list">
      {list.map((x, i) => (
        <div key={i} className="flex gap-2 items-center" data-testid={`social-${i}`}>
          <input value={x.label} onChange={(e) => upd(i, { label: e.target.value })} placeholder="Instagram" className={`${inputCls} w-40`} data-testid={`social-${i}-label`} />
          <input value={x.url} onChange={(e) => upd(i, { url: e.target.value })} placeholder="https://instagram.com/craftendemic" className={inputCls} data-testid={`social-${i}-url`} />
          <ListControls list={list} i={i} set={set} />
        </div>
      ))}
      <button onClick={() => set([...list, { label: "", url: "https://" }])} className="text-xs flex items-center gap-1 border border-[#3B220C] px-3 py-2" data-testid="add-social-btn"><Plus size={12} />Add link</button>
    </div>
  );
}

export default function Website() {
  const { loadSite } = useApp();
  const [s, setS] = useState(null);
  const [cols, setCols] = useState([]);
  useEffect(() => {
    api.get("/admin/site").then((r) => setS(r.data));
    api.get("/collections").then((r) => setCols(r.data));
  }, []);
  if (!s) return null;
  const setSlide = (i, patch) => setS({ ...s, slides: s.slides.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const setCol = (i, patch) => setCols(cols.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const save = async () => {
    try {
      await api.put("/admin/site", s);
      await api.put("/admin/collections", cols);
      toast.success("Website saved");
      loadSite();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-website">
      <AdminTitle action={<Btn onClick={save} data-testid="website-save-btn">Save all changes</Btn>}>Website</AdminTitle>
      <Card title="Logo & favicon" testid="website-brand">
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Logo (shown in header, invoices and certificates)">
            <div className="flex items-center gap-3">
              <img src={fileUrl(s.logo_url)} alt="logo" className="w-14 h-14 object-contain border" data-testid="website-logo-preview" />
              <Uploader accept="image/png,image/jpeg" label="Upload logo" onDone={(d) => setS({ ...s, logo_url: d.url, logo_path: d.path })} testid="website-logo-upload" />
            </div>
          </Field>
          <Field label="Favicon (browser tab icon)">
            <div className="flex items-center gap-3">
              <img src={fileUrl(s.favicon_url)} alt="favicon" className="w-10 h-10 object-contain border" data-testid="website-favicon-preview" />
              <Uploader accept="image/png,image/x-icon" label="Upload favicon" onDone={(d) => setS({ ...s, favicon_url: d.url })} testid="website-favicon-upload" />
            </div>
          </Field>
        </div>
      </Card>
      <Card title="Font" testid="website-font">
        <Field label="Font for the whole website (transitional serif, upright)">
          <select value={s.font_family} onChange={(e) => setS({ ...s, font_family: e.target.value })} className={inputCls} data-testid="website-font-select">
            {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field>
      </Card>
      <Card title="Login page photo" testid="website-login-image">
        <ImageField value={s.login_image} onChange={(login_image) => setS({ ...s, login_image })} testid="login-image" />
      </Card>
      <Card title="Social media & other websites (shown in footer)" testid="website-socials">
        <Socials list={s.socials || []} set={(socials) => setS({ ...s, socials })} />
      </Card>
      <Card title="Homepage slideshow" testid="website-slides">
        {s.slides.map((sl, i) => (
          <div key={i} className="border border-[#E6DFD5] p-4 space-y-3 bg-[#FAFAFA]" data-testid={`slide-${i}`}>
            <div className="flex justify-between text-xs text-[#6E6259]"><span>Slide {i + 1}</span><ListControls list={s.slides} i={i} set={(slides) => setS({ ...s, slides })} /></div>
            <ImageField value={sl.image} onChange={(image) => setSlide(i, { image })} testid={`slide-${i}-image`} />
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Small text above"><input value={sl.kicker} onChange={(e) => setSlide(i, { kicker: e.target.value })} className={inputCls} /></Field>
              <Field label="Headline"><input value={sl.title} onChange={(e) => setSlide(i, { title: e.target.value })} className={inputCls} data-testid={`slide-${i}-title`} /></Field>
              <Field label="Button text"><input value={sl.cta_label} onChange={(e) => setSlide(i, { cta_label: e.target.value })} className={inputCls} /></Field>
              <Field label="Button link"><input value={sl.cta_link} onChange={(e) => setSlide(i, { cta_link: e.target.value })} placeholder="/shop" className={inputCls} /></Field>
            </div>
          </div>
        ))}
        <button onClick={() => setS({ ...s, slides: [...s.slides, { image: "", kicker: "", title: "", cta_label: "", cta_link: "/shop" }] })} className="text-xs flex items-center gap-1 border border-[#3B220C] px-3 py-2" data-testid="add-slide-btn"><Plus size={12} />Add slide</button>
      </Card>
      <Card title="Product collections" testid="website-collections">
        {cols.map((c, i) => (
          <div key={i} className="border border-[#E6DFD5] p-4 space-y-3 bg-[#FAFAFA]" data-testid={`collection-${i}`}>
            <div className="flex justify-between text-xs text-[#6E6259]"><span className="font-mono">{c.slug}</span><ListControls list={cols} i={i} set={setCols} /></div>
            <ImageField value={c.image} onChange={(image) => setCol(i, { image })} testid={`collection-${i}-image`} />
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Title"><input value={c.title} onChange={(e) => setCol(i, { title: e.target.value })} className={inputCls} data-testid={`collection-${i}-title`} /></Field>
              <Field label="Title (ID — shown when site is in Indonesian)"><input value={c.title_id} onChange={(e) => setCol(i, { title_id: e.target.value })} className={inputCls} /></Field>
              <Field label="Slug (URL)"><input value={c.slug} onChange={(e) => setCol(i, { slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} className={inputCls} /></Field>
            </div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!c.coming_soon} onCheckedChange={(v) => setCol(i, { coming_soon: v })} />Coming soon badge</label>
          </div>
        ))}
        <button onClick={() => setCols([...cols, { slug: `collection-${cols.length + 1}`, title: "", title_id: "", image: "", coming_soon: false }])} className="text-xs flex items-center gap-1 border border-[#3B220C] px-3 py-2" data-testid="add-collection-btn"><Plus size={12} />Add collection</button>
      </Card>
      <Card title="About us page" testid="website-about">
        <Field label="Headline"><input value={s.about.title} onChange={(e) => setS({ ...s, about: { ...s.about, title: e.target.value } })} className={inputCls} data-testid="about-title-input" /></Field>
        <Field label="Story (blank line = new paragraph)"><textarea rows={6} value={s.about.body} onChange={(e) => setS({ ...s, about: { ...s.about, body: e.target.value } })} className={inputCls} data-testid="about-body-input" /></Field>
        <ImageField value={s.about.image} onChange={(image) => setS({ ...s, about: { ...s.about, image } })} testid="about-image" />
      </Card>
    </div>
  );
}
